import type { LinkProps, LinkRel, LinkTarget } from '@wix/editor-elements-definitions';
import type { CorvidSDKPropsFactory } from '@wix/editor-elements-types/corvid';
import type { LinkUtils, ISetProps } from '@wix/thunderbolt-symbols';
import { assert } from '../assert';
import { messageTemplates } from '../messages';
import { reportError } from '../reporters';
import { withValidation } from '../validations';

export interface ILinkPropsSDKProps {
  link?: LinkProps;
  preventLinkNavigation?: boolean;
}

export interface ILinkPropsSDK {
  link?: string;
  target: string;
  preventLinkNavigation?: boolean;
  rel: string;
}

export const setLink = (
  url: string,
  target: LinkTarget | undefined,
  linkUtils: LinkUtils,
  setProps: ISetProps,
  rel?: string,
) => {
  if (assert.isNil(url) || url === '') {
    setProps({
      link: undefined,
    });
    return;
  }

  try {
    setProps({
      link: linkUtils.getLinkProps(url, target, rel),
    });
  } catch (e) {
    reportError(
      `The link property that is passed to the link method cannot be set to the value "${url}" as this is not a supported link type.`,
    );
  }
};

export const getLink = (props: ILinkPropsSDKProps, linkUtils: LinkUtils) =>
  props.link ? linkUtils.getLink(props.link) : '';

const _linkPropsSDKFactory: CorvidSDKPropsFactory<
  ILinkPropsSDKProps,
  ILinkPropsSDK
> = ({ setProps, props, platformUtils: { linkUtils } }) => {
  return {
    set link(url: string) {
      setLink(url, props.link?.target, linkUtils, setProps, props.link?.rel);
    },
    get link() {
      return getLink(props, linkUtils);
    },
    set target(target: LinkTarget) {
      setProps({
        link: { ...props.link, target },
      });
    },
    get target() {
      return props.link?.target ?? '_blank';
    },
    set preventLinkNavigation(preventLinkNavigation: boolean) {
      setProps({ preventLinkNavigation });
    },
    get preventLinkNavigation() {
      return props.preventLinkNavigation ?? false;
    },
    set rel(rel: string) {
      setProps({
        link: {
          ...props.link,
          rel: rel !== '' ? rel : undefined,
        },
      });
    },
    get rel() {
      return props.link?.rel ?? '';
    },
  };
};

export const linkPropsSDKFactory = withValidation(
  _linkPropsSDKFactory,
  {
    type: ['object'],
    properties: {
      link: { type: ['string', 'nil'], warnIfNil: true },
      target: { type: ['string', 'nil'], warnIfNil: true },
      preventLinkNavigation: { type: ['boolean'] },
      rel: { type: ['string'] },
    },
  },
  {
    target: [
      (target: string) => {
        if (target === '_blank' || target === '_self') {
          return true;
        }

        reportError(messageTemplates.error_target_w_photo({ target }));

        if (assert.isNil(target)) {
          return true;
        }
        return false;
      },
    ],
    rel: [
      (rel: string) => {
        if (rel === '') {
          return true;
        }

        const VALID_KEYWORDS: { readonly [Rel in LinkRel]: Rel } = {
          noreferrer: 'noreferrer',
          noopener: 'noopener',
          nofollow: 'nofollow',
          sponsored: 'sponsored',
        };
        const validKeywords: ReadonlyArray<string> =
          Object.values(VALID_KEYWORDS);

        const keywords: ReadonlyArray<string> = rel.split(' ');
        for (let index = 0; index < keywords.length; index += 1) {
          const keyword = keywords[index];
          if (
            !validKeywords.includes(keyword) ||
            index !== keywords.lastIndexOf(keyword)
          ) {
            reportError(
              messageTemplates.error_invalid_rel({ rel, validKeywords }),
            );
            return false;
          }
        }

        return true;
      },
    ],
  },
);
